/**
 * Input Sanitization and Validation Utility
 * 
 * Provides XSS protection and input validation without breaking existing functionality
 */

class InputSanitizer {
    /**
     * Sanitize HTML input to prevent XSS attacks
     * @param {string} input - Raw input string
     * @returns {string} Sanitized string
     */
    static sanitizeHTML(input) {
        if (typeof input !== 'string') return input;
        
        const map = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#x27;',
            '/': '&#x2F;',
            '`': '&#96;',
            '=': '&#x3D;'
        };
        
        return input.replace(/[&<>"'`=\/]/g, (s) => map[s]);
    }

    /**
     * Sanitize user input for API requests
     * @param {any} data - Data to sanitize
     * @returns {any} Sanitized data
     */
    static sanitizeForAPI(data) {
        if (typeof data === 'string') {
            return this.sanitizeHTML(data.trim());
        }
        
        if (Array.isArray(data)) {
            return data.map(item => this.sanitizeForAPI(item));
        }
        
        if (data && typeof data === 'object') {
            const sanitized = {};
            for (const [key, value] of Object.entries(data)) {
                sanitized[key] = this.sanitizeForAPI(value);
            }
            return sanitized;
        }
        
        return data;
    }

    /**
     * Validate email format
     * @param {string} email - Email to validate
     * @returns {boolean} Is valid email
     */
    static isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    /**
     * Validate phone number (digits only)
     * @param {string} phone - Phone number to validate
     * @returns {boolean} Is valid phone
     */
    static isValidPhone(phone) {
        const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
        return phoneRegex.test(phone);
    }

    /**
     * Remove potentially dangerous characters from file names
     * @param {string} filename - Original filename
     * @returns {string} Safe filename
     */
    static sanitizeFilename(filename) {
        return filename.replace(/[^a-zA-Z0-9\.\-_]/g, '_');
    }

    /**
     * Validate and sanitize URL
     * @param {string} url - URL to validate
     * @returns {string|null} Sanitized URL or null if invalid
     */
    static sanitizeURL(url) {
        try {
            const urlObj = new URL(url);
            // Only allow http and https protocols
            if (!['http:', 'https:'].includes(urlObj.protocol)) {
                return null;
            }
            return urlObj.toString();
        } catch (e) {
            return null;
        }
    }

    /**
     * Generate CSRF token
     * @returns {string} CSRF token
     */
    static generateCSRFToken() {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    }

    /**
     * Validate CSRF token
     * @param {string} token - Token to validate
     * @param {string} sessionToken - Session token from storage
     * @returns {boolean} Is valid token
     */
    static validateCSRFToken(token, sessionToken) {
        return token === sessionToken;
    }
}

export default InputSanitizer;
